import { useCallback, useMemo } from "react";

import { getIdFromAccessId } from "../shared/access-id";

import { useJoinAsSectionAdmin } from "./hooks/use-join-as-section-admin";
import { usePageActions } from "./page";
import { useQueryParamsManager } from "./routing/hooks/use-query-params-manager";
import { getNavigationValues, pageUrl } from "./urls";

import { AssessmentStatus } from "api/api-data-types";
import { serverReleaseAssessments } from "api/api-server-actions";
import { serverGetPageAssessmentStatuses } from "api/api-server-data";
import {
  ACCESS_ID_QUERY_PARAM,
  ASSESSMENT_VIEW_QUERY_PARAM,
  SECONDARY_OVERLAY_QUERY_PARAM,
  SUBMISSION_ID_QUERY_PARAM,
} from "components/constants";
import {
  LAST_ASSESSMENT_PANEL_ACCESS_ID,
  getTotalSubmissions,
} from "components/materials/page/assessment/helpers";
import { ResponseType, useResponsesForPage } from "components/materials/page/responses";
import { showStopPresentingForAssessmentModal } from "components/materials/presentation/StopPresentingForAssessmentModal";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { showConfirmationModal } from "mds/components/Modal";
import { ORDER_BY_CREATED_DATE_DESC, useCustomOrdered } from "mds/hooks/use-ordered";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectDefaultSubmissionForUser,
  selectOfficialFullSubmissionsByPageId,
  selectSectionUserMap,
  selectShouldShowModalOnExitAssessmentMode,
  selectSubmissionsByPageId,
} from "store/selectors";
import { selectPageGroupUsersByPageId } from "store/selectors/page-groups";
import { isIndividualSubmission } from "store/selectors/type-helpers";
import { computedStateActions } from "store/slices/computed";
import { localStateActions } from "store/slices/local";
import { viewStateActions } from "store/slices/view";
import { useListSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { stopPresenting, usePresentation } from "utils/presentation";
import { useParams } from "utils/routing";

const showExitAssessmentModeModal = (
  page: PageType,
  onStopAssessing: () => void,
  count: number,
  total: number,
) => {
  const labelText = t("assessment.assessment_in_progress_modal.description", {
    context: page.assessments_published_at ? "published" : "unpublished",
    count,
    total,
  });
  return showConfirmationModal({
    title: t("assessment.assessment_in_progress_modal.title"),
    labelText,
    confirmButtonText: t("assessment.resume_assessment"),
    cancelButtonText: t("assessment.finish_later"),
    onCancel: () => onStopAssessing(),
  });
};

export const useAssessmentActions = () => {
  type AssessmentCreateOrUpdateType = {
    points: number;
    comment: string;
    page_outcomes: Record<string, number>;
    is_official: boolean;
  };
  const createAssessment = (
    params: AssessmentCreateOrUpdateType,
    assessedById: string,
    submissionId: string,
    sectionId: string,
  ) => {
    return storeApi.assessments
      .create({
        points: params.points,
        comment: params.comment,
        page_outcomes: params.page_outcomes,
        assessed_by_id: assessedById,
        section_id: sectionId,
        submission_id: submissionId,
        is_official: params.is_official,
      })
      .then(() => {
        toastSuccessMessage(t("success.toasts.create_success", { item: t(`glossary.assessment`) }));
      });
  };
  const updateAssessment = (
    assessmentId: string,
    params: AssessmentCreateOrUpdateType,
    assessedById: string,
    submissionId: string,
  ) => {
    return storeApi.assessments
      .partial_update({
        id: assessmentId,
        points: params.points,
        comment: params.comment,
        page_outcomes: params.page_outcomes,
        assessed_by_id: assessedById,
        submission_id: submissionId,
        is_official: params.is_official,
      })
      .then(() => {
        toastSuccessMessage(t("success.toasts.update_success", { item: t(`glossary.assessment`) }));
      });
  };

  const deleteAssessment = (assessmentId: string) => {
    return storeApi.assessments.destroy(assessmentId).then(() => {
      toastSuccessMessage(t("success.toasts.delete_success", { item: t(`glossary.assessment`) }));
    });
  };

  return { createAssessment, updateAssessment, deleteAssessment };
};

export const useAssessmentMode = (page: PageType) => {
  const dispatch = useAppDispatch();
  const { navigateToPage } = usePageActions();
  const { setQueryParams, deleteQueryParams } = useQueryParamsManager();
  const sectionId = page.section_id;
  const shouldShowModalOnExitAssessmentMode = useAppSelector(
    selectShouldShowModalOnExitAssessmentMode,
  );
  const { isPresenting, presentedSessionId } = usePresentation();
  const pathParams = useParams();
  const pageId = pathParams.pageId;
  const { joinAsSectionAdminIfNew } = useJoinAsSectionAdmin();

  const startAssessing = useCallback(async () => {
    if (isPresenting && presentedSessionId) {
      const shouldContinue = await showStopPresentingForAssessmentModal();
      if (!shouldContinue) {
        return;
      }
      await stopPresenting(presentedSessionId);
    }

    await joinAsSectionAdminIfNew();

    dispatch(localStateActions.setIsEditMode(false));
    dispatch(viewStateActions.setShowAssessmentSummary(false));

    if (pageId !== page.id) {
      const href = pageUrl(sectionId, page.id, {
        additionalParams: `${ASSESSMENT_VIEW_QUERY_PARAM}=1`,
      });
      navigateToPage(page, href);
    } else {
      setQueryParams({
        [ASSESSMENT_VIEW_QUERY_PARAM]: "1",
        [SECONDARY_OVERLAY_QUERY_PARAM]: null,
        [SUBMISSION_ID_QUERY_PARAM]: null,
      });
    }
  }, [
    isPresenting,
    presentedSessionId,
    dispatch,
    page,
    pageId,
    sectionId,
    navigateToPage,
    setQueryParams,
    joinAsSectionAdminIfNew,
  ]);

  const stopAssessing = useCallback(async () => {
    const statuses = await serverGetPageAssessmentStatuses({ pageIds: [page.id] });
    // Dispatch new version of assessment status for page
    dispatch(computedStateActions.savePageAssessmentStatuses(statuses));

    deleteQueryParams([
      ACCESS_ID_QUERY_PARAM,
      ASSESSMENT_VIEW_QUERY_PARAM,
      SUBMISSION_ID_QUERY_PARAM,
    ]);
  }, [deleteQueryParams, dispatch, page.id]);

  const stopAssessingAndMaybeShowAssessmentSummary = () => {
    if (page.assessments_published_at) {
      dispatch(viewStateActions.setShowAssessmentSummary(true));
    }
    stopAssessing();
  };

  const onExitAssessmentModeWithModal = (
    officialSubmissionsCount: number,
    totalSubmissionsCount: number,
  ) =>
    shouldShowModalOnExitAssessmentMode
      ? () =>
          showExitAssessmentModeModal(
            page,
            stopAssessingAndMaybeShowAssessmentSummary,
            officialSubmissionsCount,
            totalSubmissionsCount,
          )
      : stopAssessingAndMaybeShowAssessmentSummary;

  return {
    startAssessing,
    stopAssessing,
    stopAssessingAndMaybeShowAssessmentSummary,
    onExitAssessmentModeWithModal,
  };
};

export const useAssessmentPublishActions = (page: PageType) => {
  const dispatch = useAppDispatch();
  const publishGrades = async () => {
    await serverReleaseAssessments(page.id);
    toastSuccessMessage(t("success.toasts.publish_assessments_success"));
    dispatch(viewStateActions.setShowAssessmentSummary(true));
  };

  const unpublishGrades = () => {
    storeApi.pages.partial_update({ id: page.id, assessments_published_at: null });
    dispatch(
      computedStateActions.savePageAssessmentStatuses({
        [page.id]: {
          status: AssessmentStatus.ASSESSABLE,
          unassessed_late_count: 0,
          assessed_submissions_count: 0,
        },
      }),
    );
    dispatch(viewStateActions.setShowAssessmentSummary(false));
  };

  return { publishGrades, unpublishGrades };
};

export type AssessmentCountData = {
  count: number;
  total: number;
};

export type AssessmentCountDataWithNotSubmitted = AssessmentCountData & {
  notSubmittedCount: number;
};

/**
 * Hook that returns count and total without calculating notSubmittedCount.
 * Use this when you don't need notSubmittedCount.
 */
export const useAssessedCountData = (page: PageType | undefined): AssessmentCountData => {
  // Submissions must be already loaded by the parent components before using this hook.
  const submissions = useListSelector((s) => selectSubmissionsByPageId(s, page?.id ?? ""));
  const officialSubmissions = useListSelector((s) =>
    selectOfficialFullSubmissionsByPageId(s, page?.id ?? ""),
  );
  const enabledPageGroupUsers = useListSelector((s) =>
    selectPageGroupUsersByPageId(s, page?.id ?? ""),
  );

  if (!page) return { count: 0, total: 0 };

  const total = getTotalSubmissions(page, submissions, enabledPageGroupUsers);
  const count = officialSubmissions.length;

  return { count, total };
};

/**
 * Hook that returns count, total, and notSubmittedCount.
 * Use this when you need notSubmittedCount, but be aware it causes re-renders when a new user types in the doc because
 *  of useWorksheetAccessIds.
 */
export const useAssessedCountDataWithNotSubmitted = (
  page: PageType | undefined,
): AssessmentCountDataWithNotSubmitted => {
  const { count, total } = useAssessedCountData(page);
  const { responses } = useResponsesForPage(page);
  const notSubmittedCount = responses.filter((r) => r.id).length - total;
  return { count, total, notSubmittedCount };
};

export const useFirstAssessableResponseId = (
  page: PageType | undefined,
  responses: ResponseType[],
) => {
  const submissions = useCustomOrdered(
    useAppSelector((s) => selectSubmissionsByPageId(s, page?.id ?? "")),
    ORDER_BY_CREATED_DATE_DESC,
  );
  const sectionUserMap = useAppSelector(selectSectionUserMap);

  if (!page) return undefined;

  for (const response of responses) {
    const actualId = getIdFromAccessId(response.id);

    const lastSubmission = submissions.find((submission) => {
      if (isIndividualSubmission(submission)) {
        const sectionUser = sectionUserMap[submission.section_user_id];
        return sectionUser?.user_id === actualId;
      }
      return submission.page_group_id === actualId;
    });

    if (lastSubmission && !lastSubmission.official_at) {
      return response.id;
    }
  }
};

export const useAssessmentNavData = (page: PageType | undefined) => {
  const { getQueryParam, setQueryParams } = useQueryParamsManager();
  const { responses } = useResponsesForPage(page);

  const panels = useMemo(
    () => responses.map((response) => response.id).concat([LAST_ASSESSMENT_PANEL_ACCESS_ID]),
    [responses],
  );

  const firstId = useFirstAssessableResponseId(page, responses);
  const accessParamId = getQueryParam(ACCESS_ID_QUERY_PARAM);

  const currentId = accessParamId || firstId;
  const isLastPanel = currentId === LAST_ASSESSMENT_PANEL_ACCESS_ID;

  const { nextId, prevId, nextURL, prevURL } = useMemo(
    () =>
      getNavigationValues(panels, {
        queryParam: ACCESS_ID_QUERY_PARAM,
        currentId,
      }),
    [panels, currentId],
  );

  const submissionForNextUser = useAppSelector((s) =>
    selectDefaultSubmissionForUser(s, page?.id ?? "", nextId, true),
  );

  const submissionForPrevUser = useAppSelector((s) =>
    selectDefaultSubmissionForUser(s, page?.id ?? "", prevId, true),
  );

  const memoizedNextURL = useMemo(() => {
    if (!nextURL) return null;

    const url = new URL(nextURL.toString());

    if (submissionForNextUser) {
      url.searchParams.set(SUBMISSION_ID_QUERY_PARAM, submissionForNextUser.id);
    }

    url.searchParams.set(ASSESSMENT_VIEW_QUERY_PARAM, "1");
    return url;
  }, [nextURL, submissionForNextUser]);

  const memoizedPrevURL = useMemo(() => {
    if (!prevURL) return null;

    const url = new URL(prevURL.toString());

    if (submissionForPrevUser) {
      url.searchParams.set(SUBMISSION_ID_QUERY_PARAM, submissionForPrevUser.id);
    }

    url.searchParams.set(ASSESSMENT_VIEW_QUERY_PARAM, "1");
    return url;
  }, [prevURL, submissionForPrevUser]);

  const onBack = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.preventDefault();
      e?.stopPropagation();
      setQueryParams({
        [ACCESS_ID_QUERY_PARAM]: prevId || null,
        [SUBMISSION_ID_QUERY_PARAM]: submissionForPrevUser?.id || null,
      });
    },
    [setQueryParams, prevId, submissionForPrevUser],
  );

  const onNext = useCallback(() => {
    setQueryParams({
      [ACCESS_ID_QUERY_PARAM]: nextId || null,
      [SUBMISSION_ID_QUERY_PARAM]: submissionForNextUser?.id || null,
    });
  }, [setQueryParams, nextId, submissionForNextUser]);

  return {
    onNext,
    onBack,
    nextURL: memoizedNextURL,
    prevURL: memoizedPrevURL,
    prevId,
    nextId,
    currentId,
    isLastPanel,
    submissionForNextUser,
    submissionForPrevUser,
  };
};
